import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classnames from 'classnames';

import useImageSupportsWebP from '@hooks/images/useImageSupportsWebP';
import placeHolder from '@assets/images/loading-header.png';
import getDoubleDpiSrc from '@utils/images/getDoubleDpiSrc';
import Image from './components/Image';
import WebPImage from './components/WebPImage';
import ErrorImage from './components/ErrorImage';
import FallbackImage from './components/FallbackImage';

function CustomImage({
  src,
  placeholder,
  className,
  alt,
  onClick,
  id,
  customClass,
  onLoad,
  showErrorImage,
  ariaHidden,
}) {
  const [showFallback, setShowFallback] = useState(false);
  const imageSupportsWebP = useImageSupportsWebP({ src });

  const props = useMemo(() => {
    const data = {
      alt,
      onClick: e => onClick && onClick(e),
      onError: e => {
        e.target.style.visibility = 'hidden';
        setShowFallback(true);
      },
      onLoad: e => onLoad && onLoad(e),
    };
    if (id) data.id = id;
    return data;
  }, [alt, id, onClick, onLoad]);

  const classes = [className, customClass];

  if (showFallback || !src) {
    return showErrorImage ? (
      <ErrorImage
        aria-hidden={ariaHidden}
        className={classnames('lazyload', ...classes)}
        {...props}
      />
    ) : (
      <FallbackImage
        aria-hidden={ariaHidden}
        className={classnames(...classes)}
        {...props}
      />
    );
  }

  return (
    <picture>
      {imageSupportsWebP && <WebPImage src={src} />}
      <Image
        src={placeholder}
        className={classnames('lazyload', ...classes)}
        {...props}
        data-src={src}
        data-srcset={imageSupportsWebP ? getDoubleDpiSrc(src) : src}
        aria-hidden={ariaHidden}
      />
    </picture>
  );
}

CustomImage.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  src: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  customClass: PropTypes.string,
  alt: PropTypes.string,
  onClick: PropTypes.func,
  onLoad: PropTypes.func,
  showErrorImage: PropTypes.bool,
  ariaHidden: PropTypes.bool,
};

CustomImage.defaultProps = {
  alt: '',
  customClass: '',
  placeholder: placeHolder,
  showErrorImage: false,
};

export default styled(CustomImage)``;
